import React from 'react';

import { Box, Typography } from '@mui/material';

import RichardMonogram from '~/pages/AskRichardUnified/chat/RichardMonogram';

interface AskRichardWorkingProps {
  /** What is happening, in the reader's terms. */
  label: string;
  /** Set inside a control that already has its own label. */
  iconOnly?: boolean;
}

/**
 * The wait on askRichard, shown the way askRichard shows it: the R mark
 * itself, softly pulsing, rather than a generic spinner or dot.
 *
 * Every Lens surface that asks the model something used to say so in text
 * alone, so a request that takes twenty seconds looked identical to one that
 * had silently done nothing. The mark carries its own circle and colour, so
 * it needs no Avatar wrapper and no background of its own. Reduced motion
 * gets the same still mark, since the pulse is decoration, not information.
 */
export function AskRichardWorking({ label, iconOnly = false }: AskRichardWorkingProps) {
  const indicator = (
    <RichardMonogram
      titleAccess={label}
      sx={{
        animation: 'askRichardWorkingPulse 1.6s ease-in-out infinite',
        fontSize: 18,
        '@keyframes askRichardWorkingPulse': {
          '0%, 100%': { opacity: 0.5 },
          '50%': { opacity: 1 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          opacity: 1,
        },
      }}
    />
  );

  if (iconOnly) {
    return indicator;
  }

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      {indicator}
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
    </Box>
  );
}

export default AskRichardWorking;
